"use client";

import { c as _c } from "react/compiler-runtime";
import { Badge, Button, Card, CardContent, CardDescription, CardHeader, CardTitle, Header, Separator, Tooltip, TooltipContent, TooltipTrigger } from "@szum-tech/design-system";
import { ActivityIcon, CodeIcon, ExternalLinkIcon, FileTextIcon, GitBranchIcon, LayersIcon, type LucideIcon, PaletteIcon, RocketIcon, SearchIcon, ServerIcon, ShieldCheckIcon, SparklesIcon, TagIcon, TerminalIcon, TestTube2Icon } from "lucide-react";
import Image from "next/image";
import { GithubIcon } from "~/components/ui/icons/github";
import { ThemeToggle } from "~/components/ui/theme-toggle";
import { FEATURES, type FeatureIconName, QUICK_START_STEPS, SCRIPTS, SZUM_TECH_PACKAGES, type SzumTechIconName, TECH_STACK } from "~/constants";
const FEATURE_ICONS: Record<FeatureIconName, LucideIcon> = {
  ActivityIcon,
  FileTextIcon,
  GitBranchIcon,
  LayersIcon,
  RocketIcon,
  SearchIcon,
  ServerIcon,
  ShieldCheckIcon,
  TestTube2Icon
};
const SZUM_TECH_ICONS: Record<SzumTechIconName, LucideIcon> = {
  PaletteIcon,
  ShieldCheckIcon,
  SparklesIcon,
  TagIcon
};
export default function Home() {
  const $ = _c(43);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <a className="sr-only focus:not-sr-only focus:absolute focus:top-4 focus:left-4 focus:z-50 focus:rounded focus:bg-primary focus:px-4 focus:py-2 focus:text-primary-foreground focus:shadow-lg" href="#main-content">Skip to main content</a>;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  let t1;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <div className="flex items-center gap-2"><SparklesIcon className="size-5 text-primary" /><span className="font-semibold text-body-sm">Szum-Tech</span></div>;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <ThemeToggle />;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <GithubIcon />;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  let t4;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = <Header><div className="flex w-full items-center justify-between">{t1}<div className="flex items-center gap-2">{t2}<Button asChild={true} endIcon={t3} size="sm" variant="outline"><a aria-label="View GitHub repository (opens in new tab)" href="https://github.com/JanSzewczyk/nextjs-szumplate" rel="noreferrer" target="_blank">GitHub</a></Button></div></div></Header>;
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  let t5;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = <Badge className="mb-4" variant="outline">Next.js 16 • React 19 • TypeScript • RSC</Badge>;
    $[5] = t5;
  } else {
    t5 = $[5];
  }
  let t6;
  let t7;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = <h1 className="mb-6 text-display-lg"><span className="text-primary">Szum-Tech</span> Next.js Template</h1>;
    t7 = <p className="mb-8 max-w-2xl text-lead">Enterprise-ready Next.js starter template with TypeScript, Tailwind CSS, comprehensive testing infrastructure, and the Szum-Tech Design System built-in.</p>;
    $[6] = t6;
    $[7] = t7;
  } else {
    t6 = $[6];
    t7 = $[7];
  }
  let t8;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = <Button asChild={true} size="lg" startIcon={<RocketIcon />}><a aria-label="Use this template (opens in new tab)" href="https://github.com/JanSzewczyk/nextjs-szumplate/generate" rel="noreferrer" target="_blank">Use This Template</a></Button>;
    $[8] = t8;
  } else {
    t8 = $[8];
  }
  let t9;
  if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = <GithubIcon />;
    $[9] = t9;
  } else {
    t9 = $[9];
  }
  let t10;
  let t11;
  if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = <section className="container py-16 md:py-24 lg:py-32" id="hero"><div className="mx-auto flex max-w-4xl flex-col items-center text-center">{t5}{t6}{t7}<div className="flex flex-col gap-4 sm:flex-row">{t8}<Button asChild={true} endIcon={t9} size="lg" variant="outline"><a aria-label="View on GitHub (opens in new tab)" href="https://github.com/JanSzewczyk/nextjs-szumplate" rel="noreferrer" target="_blank">View on GitHub</a></Button></div></div></section>;
    t11 = <Separator />;
    $[10] = t10;
    $[11] = t11;
  } else {
    t10 = $[10];
    t11 = $[11];
  }
  let t12;
  if ($[12] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = <div className="mb-12 text-center"><h2 className="mb-4 text-heading-h2">Why Choose This Template?</h2><p className="mx-auto max-w-2xl text-muted-foreground">Everything you need to build production-ready applications, pre-configured and ready to go.</p></div>;
    $[12] = t12;
  } else {
    t12 = $[12];
  }
  let t13;
  let t14;
  if ($[13] === Symbol.for("react.memo_cache_sentinel")) {
    t13 = <section className="bg-muted/50 py-16 md:py-24" id="features"><div className="container">{t12}<div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">{FEATURES.map(_temp)}</div></div></section>;
    t14 = <Separator />;
    $[13] = t13;
    $[14] = t14;
  } else {
    t13 = $[13];
    t14 = $[14];
  }
  let t15;
  if ($[15] === Symbol.for("react.memo_cache_sentinel")) {
    t15 = <div className="mb-12 text-center"><Badge className="mb-4" variant="primary">Open Source</Badge><h2 className="mb-4 text-heading-h2">Szum-Tech Ecosystem</h2><p className="mx-auto max-w-2xl text-muted-foreground">This template is powered by a suite of open-source packages designed to accelerate development and maintain consistency across projects.</p></div>;
    $[15] = t15;
  } else {
    t15 = $[15];
  }
  let t16;
  if ($[16] === Symbol.for("react.memo_cache_sentinel")) {
    t16 = <div className="grid gap-6 md:grid-cols-2">{SZUM_TECH_PACKAGES.map(_temp3)}</div>;
    $[16] = t16;
  } else {
    t16 = $[16];
  }
  let t17;
  if ($[17] === Symbol.for("react.memo_cache_sentinel")) {
    t17 = <p className="mb-4 text-body-sm text-muted-foreground">All packages are maintained by Szum-Tech and follow semantic versioning.</p>;
    $[17] = t17;
  } else {
    t17 = $[17];
  }
  let t18;
  if ($[18] === Symbol.for("react.memo_cache_sentinel")) {
    t18 = <GithubIcon className="size-4" />;
    $[18] = t18;
  } else {
    t18 = $[18];
  }
  let t19;
  let t20;
  if ($[19] === Symbol.for("react.memo_cache_sentinel")) {
    t19 = <section className="py-16 md:py-24" id="ecosystem"><div className="container">{t15}{t16}<div className="mt-10 text-center">{t17}<Button asChild={true} endIcon={t18} size="sm" variant="outline"><a aria-label="Explore all packages (opens in new tab)" href="https://github.com/JanSzewczyk" rel="noreferrer" target="_blank">Explore All Packages</a></Button></div></div></section>;
    t20 = <Separator />;
    $[19] = t19;
    $[20] = t20;
  } else {
    t19 = $[19];
    t20 = $[20];
  }
  let t21;
  if ($[21] === Symbol.for("react.memo_cache_sentinel")) {
    t21 = <div className="mb-12 text-center"><h2 className="mb-4 text-heading-h2">Tech Stack</h2><p className="mx-auto max-w-2xl text-muted-foreground">Carefully selected technologies for modern web development, all working together seamlessly.</p></div>;
    $[21] = t21;
  } else {
    t21 = $[21];
  }
  let t22;
  let t23;
  if ($[22] === Symbol.for("react.memo_cache_sentinel")) {
    t22 = <section className="bg-muted/50 py-16 md:py-24" id="tech-stack"><div className="container">{t21}<div className="space-y-12">{Object.entries(TECH_STACK).map(_temp5)}</div></div></section>;
    t23 = <Separator />;
    $[22] = t22;
    $[23] = t23;
  } else {
    t22 = $[22];
    t23 = $[23];
  }
  let t24;
  if ($[24] === Symbol.for("react.memo_cache_sentinel")) {
    t24 = <div className="mb-12 text-center"><h2 className="mb-4 text-heading-h2">Quick Start</h2><p className="mx-auto max-w-2xl text-muted-foreground">Get up and running in minutes with these simple steps.</p></div>;
    $[24] = t24;
  } else {
    t24 = $[24];
  }
  let t25;
  let t26;
  if ($[25] === Symbol.for("react.memo_cache_sentinel")) {
    t25 = <section className="py-16 md:py-24" id="quick-start"><div className="container">{t24}<div className="mx-auto grid max-w-4xl gap-6 md:grid-cols-3">{QUICK_START_STEPS.map(_temp6)}</div></div></section>;
    t26 = <Separator />;
    $[25] = t25;
    $[26] = t26;
  } else {
    t25 = $[25];
    t26 = $[26];
  }
  let t27;
  if ($[27] === Symbol.for("react.memo_cache_sentinel")) {
    t27 = <div className="mb-12 text-center"><h2 className="mb-4 text-heading-h2">Built-in Scripts</h2><p className="mx-auto max-w-2xl text-muted-foreground">All the npm scripts you need for development, testing, and deployment.</p></div>;
    $[27] = t27;
  } else {
    t27 = $[27];
  }
  let t28;
  if ($[28] === Symbol.for("react.memo_cache_sentinel")) {
    t28 = <div className="mx-auto grid max-w-4xl gap-4 sm:grid-cols-2 lg:grid-cols-3">{SCRIPTS.map(_temp7)}</div>;
    $[28] = t28;
  } else {
    t28 = $[28];
  }
  let t29;
  let t30;
  if ($[29] === Symbol.for("react.memo_cache_sentinel")) {
    t29 = <section className="bg-muted/50 py-16 md:py-24" id="scripts"><div className="container">{t27}{t28}<div className="mt-8 text-center"><p className="text-body-sm text-muted-foreground">See all available scripts in <code className="text-code">package.json</code></p></div></div></section>;
    t30 = <Separator />;
    $[29] = t29;
    $[30] = t30;
  } else {
    t29 = $[29];
    t30 = $[30];
  }
  let t31;
  let t32;
  let t33;
  if ($[31] === Symbol.for("react.memo_cache_sentinel")) {
    t31 = <SparklesIcon className="mb-6 size-12 text-primary" />;
    t32 = <h2 className="mb-4 text-heading-h2">Ready to Build Something Amazing?</h2>;
    t33 = <p className="mb-8 max-w-xl text-muted-foreground">Start your next project with the Szum-Tech template and focus on what matters most - building great features.</p>;
    $[31] = t31;
    $[32] = t32;
    $[33] = t33;
  } else {
    t31 = $[31];
    t32 = $[32];
    t33 = $[33];
  }
  let t34;
  if ($[34] === Symbol.for("react.memo_cache_sentinel")) {
    t34 = <Button asChild={true} size="lg" startIcon={<RocketIcon className="size-4" />}><a aria-label="Use this template (opens in new tab)" href="https://github.com/JanSzewczyk/nextjs-szumplate/generate" rel="noreferrer" target="_blank">Use This Template</a></Button>;
    $[34] = t34;
  } else {
    t34 = $[34];
  }
  let t35;
  if ($[35] === Symbol.for("react.memo_cache_sentinel")) {
    t35 = <CodeIcon className="size-4" />;
    $[35] = t35;
  } else {
    t35 = $[35];
  }
  let t36;
  if ($[36] === Symbol.for("react.memo_cache_sentinel")) {
    t36 = <main className="flex-1" id="main-content">{t10}{t11}{t13}{t14}{t19}{t20}{t22}{t23}{t25}{t26}{t29}{t30}<section className="bg-primary/5 py-16 md:py-24" id="cta"><div className="container"><div className="mx-auto flex max-w-3xl flex-col items-center text-center">{t31}{t32}{t33}<div className="flex flex-col gap-4 sm:flex-row">{t34}<Button asChild={true} size="lg" startIcon={t35} variant="outline"><a aria-label="Explore the code (opens in new tab)" href="https://github.com/JanSzewczyk/nextjs-szumplate" rel="noreferrer" target="_blank">Explore the Code</a></Button></div></div></div></section></main>;
    $[36] = t36;
  } else {
    t36 = $[36];
  }
  let t37;
  if ($[37] === Symbol.for("react.memo_cache_sentinel")) {
    t37 = <div className="flex items-center gap-2"><SparklesIcon className="size-5 text-primary" /><span className="font-semibold text-body-default">Szum-Tech Next.js Template</span></div>;
    $[37] = t37;
  } else {
    t37 = $[37];
  }
  let t38;
  let t39;
  let t40;
  let t41;
  if ($[38] === Symbol.for("react.memo_cache_sentinel")) {
    t38 = <span className="text-muted-foreground">{new Date().getFullYear()}</span>;
    t39 = <span className="text-muted-foreground/40">|</span>;
    t40 = <a aria-label="Visit Jan Szewczyk's GitHub profile (opens in new tab)" className="text-muted-foreground transition-colors hover:text-primary" href="https://github.com/JanSzewczyk" rel="noreferrer" target="_blank">Jan Szewczyk</a>;
    t41 = <span className="text-muted-foreground/40">|</span>;
    $[38] = t38;
    $[39] = t39;
    $[40] = t40;
    $[41] = t41;
  } else {
    t38 = $[38];
    t39 = $[39];
    t40 = $[40];
    t41 = $[41];
  }
  let t42;
  if ($[42] === Symbol.for("react.memo_cache_sentinel")) {
    t42 = <div className="flex min-h-screen flex-col">{t0}{t4}{t36}<footer className="border-border/40 border-t py-8"><div className="container flex flex-col items-center gap-4">{t37}<div className="flex items-center gap-3 text-body-sm">{t38}{t39}{t40}{t41}<a aria-label="View source code on GitHub (opens in new tab)" className="flex items-center gap-1 text-muted-foreground transition-colors hover:text-primary" href="https://github.com/JanSzewczyk/nextjs-szumplate" rel="noreferrer" target="_blank"><GithubIcon className="size-3.5" /><span>Source</span></a></div></div></footer></div>;
    $[42] = t42;
  } else {
    t42 = $[42];
  }
  return t42;
}
function _temp7(script) {
  return <Card className="group bg-card transition-shadow hover:shadow-md" key={script.command}><CardContent className="p-4"><div className="mb-2 flex items-center gap-2"><TerminalIcon className="size-4 text-primary" /><code className="text-code">{script.command}</code></div><p className="text-mute">{script.description}</p></CardContent></Card>;
}
function _temp6(item) {
  return <Card className="relative overflow-hidden bg-card" key={item.step}><div className="absolute -top-4 -left-2 font-bold text-8xl text-primary/10">{item.step}</div><CardHeader className="relative"><CardTitle className="flex items-center gap-2 text-heading-h3"><span className="flex size-8 items-center justify-center rounded-full bg-primary font-bold text-primary-foreground text-sm">{item.step}</span>{item.title}</CardTitle><CardDescription>{item.description}</CardDescription></CardHeader><CardContent className="relative"><div className="rounded-lg bg-muted p-3"><code className="break-all font-mono text-body-sm">{item.command}</code></div></CardContent></Card>;
}
function _temp5(t0) {
  const [key, category] = t0;
  return <div key={key}><div className="mb-6 flex items-center gap-3"><Badge variant={category.variant}>{category.label}</Badge><Separator className="flex-1" /></div><ul className="flex flex-wrap justify-center gap-4 md:justify-start">{category.items.map(_temp4)}</ul></div>;
}
function _temp4(tech) {
  return <li key={tech.href}><Tooltip><TooltipTrigger asChild={true}><a aria-label={`Learn more about ${tech.name} (opens in new tab)`} className="flex h-20 w-36 items-center justify-center rounded border bg-card transition-all hover:border-primary/50 hover:bg-accent hover:shadow-md focus:outline-none focus:ring" href={tech.href} rel="noreferrer" target="_blank"><Image alt={`${tech.name} logo`} className="p-3 grayscale transition-all hover:grayscale-0" height={60} src={tech.src} width={120} /></a></TooltipTrigger><TooltipContent side="bottom">{tech.name} - {tech.description}</TooltipContent></Tooltip></li>;
}
function _temp3(pkg) {
  const Icon_0 = SZUM_TECH_ICONS[pkg.iconName];
  return <Card className="group relative overflow-hidden transition-all hover:shadow" key={pkg.packageName}><div className="absolute inset-0 bg-linear-to-br from-primary/5 via-primary/2 to-transparent opacity-0 transition-opacity duration-500 group-hover:opacity-100" /><CardHeader className="relative"><div className="mb-4 flex items-start justify-between"><div className="flex size-12 items-center justify-center rounded-xl bg-primary/10"><Icon_0 className="size-6 text-primary" /></div><div className="flex gap-2">{pkg.docsUrl && <Tooltip><TooltipTrigger asChild={true}><Button asChild={true} size="sm" variant="ghost"><a aria-label={`View documentation for ${pkg.name} (opens in new tab)`} href={pkg.docsUrl} rel="noreferrer" target="_blank"><ExternalLinkIcon aria-hidden="true" className="size-4" /></a></Button></TooltipTrigger><TooltipContent>Documentation</TooltipContent></Tooltip>}<Tooltip><TooltipTrigger asChild={true}><Button asChild={true} size="sm" variant="ghost"><a aria-label={`View ${pkg.name} on GitHub (opens in new tab)`} href={pkg.githubUrl} rel="noreferrer" target="_blank"><GithubIcon className="size-4" /></a></Button></TooltipTrigger><TooltipContent>GitHub Repository</TooltipContent></Tooltip></div></div><CardTitle className="text-heading-h3">{pkg.name}</CardTitle><code className="text-code text-primary/80 text-sm">{pkg.packageName}</code></CardHeader><CardContent className="relative"><CardDescription className="mb-4 text-body-default">{pkg.description}</CardDescription><div className="flex flex-wrap gap-2">{pkg.features.map(_temp2)}</div></CardContent></Card>;
}
function _temp2(feature_0) {
  return <Badge className="text-xs" key={feature_0} variant="secondary">{feature_0}</Badge>;
}
function _temp(feature) {
  const Icon = FEATURE_ICONS[feature.iconName];
  return <Card className="bg-card transition-shadow hover:shadow-lg" key={feature.title}><CardHeader><div className="mb-4 flex size-12 items-center justify-center rounded-lg bg-primary/10"><Icon className="size-6 text-primary" /></div><CardTitle className="text-heading-h3">{feature.title}</CardTitle></CardHeader><CardContent><CardDescription className="text-body-default">{feature.description}</CardDescription></CardContent></Card>;
}